import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/电影" },
  { label: "电视剧", to: "/category/电视剧" },
  { label: "综艺", to: "/category/综艺" },
  { label: "资讯", to: "/news/行业新闻" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

type SiteHeaderProps = {
  transparent?: boolean;
  scrolled?: boolean;
};

export default function SiteHeader({ transparent = false, scrolled = false }: SiteHeaderProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const onSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMenuOpen(false);
  };

  const solid = !transparent || scrolled;
  const textColor = solid ? "text-foreground-900" : "text-background-50";
  const subTextColor = solid ? "text-foreground-600" : "text-background-100";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50">
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-heading font-bold text-lg ${textColor}`}>八戒影视</span>
            <span className={`text-[10px] tracking-widest ${subTextColor}`}>BAJIE MOVIE</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer ${
                solid ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100" : "text-background-50 hover:bg-white/15"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <form onSubmit={onSearch} className="relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${solid ? "text-foreground-400" : "text-background-100"}`}></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、资讯"
              className={`w-44 lg:w-56 pl-9 pr-3 py-2 text-sm rounded-md outline-none transition-colors ${
                solid
                  ? "bg-background-100 border border-background-200 text-foreground-800 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  : "bg-white/15 border border-white/25 text-background-50 placeholder:text-background-100 focus:bg-white/25"
              }`}
            />
          </form>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开导航菜单"
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
            solid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-white/15"
          }`}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 pb-4 pt-2">
          <form onSubmit={onSearch} className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、资讯"
              className="w-full pl-9 pr-3 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-800 outline-none focus:border-primary-400"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="px-3 py-2.5 text-sm rounded-md bg-background-100 text-foreground-700 hover:text-primary-600 cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}